import { Box, Grid, Typography } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReportAsset } from 'shared/types';
import { Report } from 'features/reports/report.types';
import { ReportableGeoEvent } from 'features/reports/components/ReportForm/types';
import { GeoEventType } from 'features/geoEvents/geoEvent.types';
import { MediaAlertCameraForm } from './MediaAlertCameraForm';
import { AssetTypeField } from './AssetField';
import { SocialEmbedField } from './SocialEmbedField';
import { ImageForm } from './ImageForm';

type MediaFormProps = {
  geoEvent?: ReportableGeoEvent;
  geoEventType?: GeoEventType;
  report?: Report;
  latFieldName: string;
  lngFieldName: string;
  azFieldName: string;
  allowedAssetTypes?: ReportAsset[];
  mediaAttribution?: string | null;
  isReviewing?: boolean;
};

export const MediaForm = (props: MediaFormProps): JSX.Element => {
  const {
    geoEvent,
    geoEventType,
    report,
    latFieldName,
    lngFieldName,
    azFieldName,
    allowedAssetTypes,
    mediaAttribution,
    isReviewing,
  } = props;
  const { t } = useTranslation();
  const { control } = useFormContext();

  const asset: ReportAsset = useWatch({ control, name: 'asset' }) ?? null;
  const mediaUrl: string =
    useWatch({
      control,
      name: 'mediaUrl',
      disabled: !report && !isReviewing,
    }) ?? '';

  const isReportUpdate = Boolean(geoEvent);

  // Attribution is only present on alert camera media, not user uploads
  const editingMediaAlertCamera =
    Boolean(mediaUrl) &&
    (Boolean(mediaAttribution) || Boolean(report?.media?.[0]?.attribution));

  return (
    <>
      <Grid size={12}>
        <Box sx={{ marginTop: isReportUpdate ? 0 : 2 }}>
          <Typography variant="h3">
            {t('addIncidentReport.sections.media')}
          </Typography>
        </Box>
      </Grid>

      <Grid size={12}>
        <Controller
          name="asset"
          control={control}
          render={(renderProps) => (
            <AssetTypeField<'asset'>
              {...renderProps}
              latFieldName={latFieldName}
              lngFieldName={lngFieldName}
              azFieldName={azFieldName}
              report={report}
              allowedAssetTypes={allowedAssetTypes}
            />
          )}
        />
      </Grid>

      {(asset === 'socialEmbed' || Boolean(report?.embedUrl)) && (
        <Grid size={12}>
          <Controller
            name="embedUrl"
            control={control}
            render={(renderProps) => (
              <SocialEmbedField<'embedUrl'> {...renderProps} />
            )}
          />
        </Grid>
      )}

      {(asset === 'image' || Boolean(mediaUrl)) && (
        <Grid size={12}>
          <ImageForm
            latFieldName={latFieldName}
            lngFieldName={lngFieldName}
            azFieldName={azFieldName}
            geoEvent={geoEvent}
            geoEventType={geoEventType}
            report={report}
            mediaUrl={mediaUrl}
            asset={asset}
            allowCoordinates={!editingMediaAlertCamera}
            isReviewing={isReviewing}
            attribution={mediaAttribution}
          />
        </Grid>
      )}

      {asset === 'alertCamera' && !editingMediaAlertCamera && (
        <Grid size={12}>
          <MediaAlertCameraForm />
        </Grid>
      )}
    </>
  );
};
